import { z } from 'zod';
import { UuidSchema } from '../_shared/ids.js';
import { Paginated, PaginationQuery } from '../_shared/pagination.js';
import { ArticleBodySchema, articleBodyIsEmpty } from './article-body.js';
import { ArticleStatusSchema } from './enums.js';
import { MediaDTO } from './media.js';
import { UrlKeySchema } from './url-key.js';
/**
 * Notas del medio. Contratos compartidos entre la API y el panel.
 *
 * Reglas del dominio que el contrato deja ver:
 *
 *  - `publishedAt` **se setea al publicar y no cambia más**, ni al reeditar ni al archivar y
 *    volver a publicar: es parte de la dirección pública (`/2026/07/una-nota`), y una URL que
 *    se mueve es un enlace roto en cada red social donde se compartió.
 *  - `urlKey` se deriva del título mientras la nota está en borrador y **se congela al
 *    publicar**, por lo mismo. No es un campo del formulario.
 *  - Una nota en vivo **no se guarda en silencio**: los cambios se aplican recién al publicar
 *    (ver `PublishArticleRequest`), así nada sale al sitio a medio escribir.
 *  - Todo pedido de escritura lleva `expectedUpdatedAt`: si otra persona guardó en el medio,
 *    el servidor rechaza en vez de pisar su trabajo.
 */
export const ARTICLE_TITLE_MAX = 200;
export const ARTICLE_EXCERPT_MAX = 220;
/** Límite duro de guardado. El de publicación (60) es una recomendación con contador. */
export const ARTICLE_META_TITLE_MAX = 200;
export const ARTICLE_META_TITLE_RECOMMENDED = 60;
/** Guardar admite hasta 500; **publicar exige 160 o menos** (lo pide el diseño). */
export const ARTICLE_META_DESCRIPTION_MAX = 500;
export const ARTICLE_META_DESCRIPTION_LIMIT = 160;
/** Requisitos que se tachan en el panel derecho del editor. */
export const ArticleRequisitoSchema = z.enum([
    'titulo',
    'cuerpo',
    'portada',
    'portadaAlt',
    'categoria',
    'metaDescription',
]);
/** Un requisito incumplido, tal como lo devuelve el 422 al intentar publicar. */
export const ArticleRequisitoIncumplido = z
    .object({
    campo: ArticleRequisitoSchema,
    motivo: z.string(),
})
    .strict();
export const ArticleAuthor = z
    .object({ id: UuidSchema, nombre: z.string() })
    .strict();
export const ArticleCategoryRef = z
    .object({ id: UuidSchema, name: z.string() })
    .strict();
/** Fila de bandeja. No trae el cuerpo: un listado no necesita el texto de 40 notas. */
export const ArticleListItem = z
    .object({
    id: UuidSchema,
    title: z.string(),
    urlKey: UrlKeySchema,
    status: ArticleStatusSchema,
    publishedAt: z.string().datetime().nullable(),
    updatedAt: z.string().datetime(),
    author: ArticleAuthor,
    categories: z.array(ArticleCategoryRef),
    /** Miniatura de portada; `null` si todavía no tiene o si el bucket no es público. */
    coverUrl: z.string().nullable(),
    /** Lo que falta para poder publicarla. Alimenta el "falta portada" de Mi zona. */
    requisitosPendientes: z.array(ArticleRequisitoSchema),
})
    .strict();
export const ArticleListResponse = Paginated(ArticleListItem);
/** La nota completa, tal como la carga el editor. */
export const ArticleDetail = z
    .object({
    id: UuidSchema,
    title: z.string(),
    urlKey: UrlKeySchema,
    excerpt: z.string().nullable(),
    body: ArticleBodySchema,
    status: ArticleStatusSchema,
    publishedAt: z.string().datetime().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    author: ArticleAuthor,
    cover: MediaDTO.nullable(),
    ogImage: MediaDTO.nullable(),
    categoryIds: z.array(UuidSchema),
    metaTitle: z.string().nullable(),
    metaDescription: z.string().nullable(),
    canonicalUrl: z.string().nullable(),
    requisitosPendientes: z.array(ArticleRequisitoSchema),
})
    .strict();
/**
 * "Nota nueva" crea un borrador vacío y abre el editor: el título se escribe adentro, no en
 * un diálogo previo.
 */
export const CreateArticleRequest = z
    .object({
    title: z.string().trim().max(ARTICLE_TITLE_MAX).optional(),
})
    .strict();
/**
 * Cambios de la nota. Todos los campos son opcionales (el autoguardado manda lo que tocó),
 * salvo `expectedUpdatedAt`.
 *
 * `null` limpia el campo; ausente lo deja como está. `coverAlt` no es de la nota sino de la
 * imagen: el servidor lo escribe en `Media.alt` para que el editor guarde todo de una.
 */
export const ArticleContentFields = z
    .object({
    title: z.string().trim().max(ARTICLE_TITLE_MAX),
    excerpt: z.string().trim().max(ARTICLE_EXCERPT_MAX).nullable(),
    body: ArticleBodySchema,
    coverImageId: UuidSchema.nullable(),
    coverAlt: z.string().trim().max(300).nullable(),
    ogImageId: UuidSchema.nullable(),
    categoryIds: z.array(UuidSchema).max(20),
    metaTitle: z.string().trim().max(ARTICLE_META_TITLE_MAX).nullable(),
    metaDescription: z.string().trim().max(ARTICLE_META_DESCRIPTION_MAX).nullable(),
    canonicalUrl: z
        .string()
        .trim()
        .url()
        .refine((u) => /^https?:\/\//i.test(u), 'solo http o https')
        .nullable(),
})
    .partial();
export const UpdateArticleRequest = ArticleContentFields.extend({
    /** `updatedAt` que el editor tenía cargado: el bloqueo optimista. */
    expectedUpdatedAt: z.string().datetime(),
}).strict();
/**
 * Publicar lleva **el contenido completo**, no solo la orden. Es lo que permite que una nota
 * en vivo se edite sin que nada salga al sitio hasta apretar el botón: los cambios y el cambio
 * de estado entran en la misma transacción.
 */
export const PublishArticleRequest = UpdateArticleRequest;
/**
 * El borrado va por query string y no por body: un `DELETE` con cuerpo es incómodo de emitir
 * desde el browser y de leer en un log. Igual lleva el bloqueo optimista.
 */
export const DeleteArticleQuery = z
    .object({ expectedUpdatedAt: z.string().datetime() })
    .strict();
export const ArchiveArticleRequest = z
    .object({ expectedUpdatedAt: z.string().datetime() })
    .strict();
/** Booleano que llega por query string (`?mine=true`). */
const QueryBoolean = z
    .union([z.boolean(), z.enum(['true', 'false'])])
    .transform((v) => v === true || v === 'true');
export const ListArticlesQuery = PaginationQuery.extend({
    /** Texto libre: busca en el título. */
    q: z.string().trim().max(120).optional(),
    status: ArticleStatusSchema.optional(),
    categoryId: UuidSchema.optional(),
    authorId: UuidSchema.optional(),
    /** `true` acota al usuario del token. Es lo que hace "Mi zona". */
    mine: QueryBoolean.default(false),
}).strict();
/** Contadores de los chips de filtro y de los indicadores del menú. */
export const ArticleCounts = z
    .object({
    total: z.number().int().min(0),
    draft: z.number().int().min(0),
    published: z.number().int().min(0),
    archived: z.number().int().min(0),
})
    .strict();
/**
 * Los contadores aceptan la misma búsqueda que el listado: si el chip dijera "Borradores (37)"
 * mientras la tabla filtrada muestra 2, el número dejaría de servir para decidir nada.
 */
export const ArticleCountsQuery = z
    .object({
    mine: QueryBoolean.default(false),
    q: z.string().trim().max(120).optional(),
})
    .strict();
export const ARTICLE_ERROR_CODES = {
    notFound: 'NOTA_NO_ENCONTRADA',
    /** Otra persona guardó primero. `details.updatedAt` trae la versión que hay en el server. */
    modificada: 'NOTA_MODIFICADA',
    /** Una nota en vivo no se autoguarda: sus cambios viajan con el publish. */
    enVivo: 'NOTA_EN_VIVO',
    /** Faltan requisitos para publicar. `details.requisitos` dice cuáles. */
    incompleta: 'NOTA_INCOMPLETA',
    transicionInvalida: 'NOTA_TRANSICION_INVALIDA',
    /** Solo un borrador se borra de verdad; lo que ya se publicó se archiva. */
    soloBorradorSeElimina: 'NOTA_SOLO_BORRADOR_SE_ELIMINA',
    /**
     * La nota es de otra persona y quien la pide es un Escritor. Es 403 y no 404 —a diferencia del
     * borrador ajeno, que no existe para nadie— porque esta nota **sí la ve** en la bandeja: negar
     * que existe sería mentirle sobre algo que tiene a la vista.
     */
    ajena: 'NOTA_AJENA',
};
/** Etiquetas de estado en pantalla (el dato sigue siendo DRAFT/PUBLISHED/ARCHIVED). */
export const ARTICLE_STATUS_LABEL = {
    DRAFT: 'BORRADOR',
    PUBLISHED: 'EN VIVO',
    ARCHIVED: 'ARCHIVADA',
};
/**
 * Motivo de cada requisito, redactado como una **cláusula** para poder enumerarlos en una sola
 * frase: "No se pudo publicar. Falta el texto alternativo de la imagen de portada y la
 * descripción para buscadores supera los 160 caracteres."
 *
 * Los que empiezan con "falta " se agrupan bajo un único "Falta" al armar la frase, así no se
 * lee "falta esto y falta lo otro".
 */
export const ARTICLE_REQUISITO_LABEL = {
    titulo: 'falta el título',
    cuerpo: 'falta el texto de la nota',
    portada: 'falta la imagen de portada',
    portadaAlt: 'falta el texto alternativo de la imagen de portada',
    categoria: 'falta elegir al menos una categoría',
    metaDescription: 'la descripción para buscadores supera los 160 caracteres',
};
/**
 * Requisitos que todavía no se cumplen, en el orden en que aparecen en el editor (así "Ir al
 * primer error" lleva al de más arriba).
 *
 * Vive en los contracts a propósito: el servidor lo usa para rechazar el publish y el panel
 * para pintar el checklist. Si fueran dos implementaciones, tarde o temprano el checklist
 * diría que está todo bien y el botón devolvería un 422.
 */
export function articleRequisitosPendientes(input) {
    const faltan = [];
    if (input.title.trim().length === 0)
        faltan.push('titulo');
    if (articleBodyIsEmpty(input.body))
        faltan.push('cuerpo');
    if (!input.coverImageId)
        faltan.push('portada');
    else if ((input.coverAlt ?? '').trim().length === 0)
        faltan.push('portadaAlt');
    if (input.categoryIds.length === 0)
        faltan.push('categoria');
    if ((input.metaDescription ?? '').trim().length > ARTICLE_META_DESCRIPTION_LIMIT) {
        faltan.push('metaDescription');
    }
    return faltan;
}
